import RMTabs from 'components/customComponents/Controls/RMTabs';
import { LabelRowWithActions } from 'components/searchAndFilter/components';
import { ITabItem, withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import React, { useEffect, useMemo } from 'react';
import { FilterViews } from '../constants';
import CollapsiblePanel from 'components/containers/collapsibleContainer';
import InfoBox from 'components/alerts/infoBox';
import {
  useSearchDetails,
  useSearchDisplayFields,
  useSearchFilters,
  useSearchTargetFields,
} from 'redux/hooks/searchnFilter';
import { IFilterDTO, SearchFieldDTO, SearchTargetDTO } from 'business/models/Models';
import { useRMDispatch } from 'redux/hooks';
import { toggleSearchFilter } from 'redux/reducers/searchnFilterReducer';
import { getNewId } from 'utils/Global';

interface Props extends WithTabBackButtonStateProps {
  selectedFilters: IFilterDTO[];
  setContentViewState: (view: FilterViews) => void;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IFilterDTO[]>>;
}
const FilterParametersContainer = (props: Props) => {
  const {
    setContentViewState,
    setHandleOnTabBackPress,
    selectedFilters,
    setSelectedFilters,
  } = props;
  const searchTargetFields = useSearchTargetFields();
  const displayFields = useSearchDisplayFields();
  const { id: searchId } = useSearchDetails();
  const filters = useSearchFilters();
  const dispatch = useRMDispatch();

  const currentParameters = useMemo(() => {
    return [...displayFields].sort((a, b) =>
      a.columnHeader.localeCompare(b.columnHeader),
    );
  }, [displayFields, filters]);

  const additionalFilters = useMemo(() => {
    return [...searchTargetFields].sort((a, b) => a.name.localeCompare(b.name));
  }, [filters, searchTargetFields]);

  const onFilterSelectionChange = (id: number) => {
    const newFilter: IFilterDTO = {
      id: getNewId(),
      searchFieldId: id,
      searchId: searchId,
      values: [],
    };
    dispatch(toggleSearchFilter(newFilter));
  };

  const onAdditionalFilterSelectionChange = (id: number) => {
    if (!selectedFilters.some((f) => f.searchFieldId === id)) {
      const newFilter: IFilterDTO = {
        id: getNewId(),
        searchFieldId: id,
        searchId: searchId,
        values: [],
      };
      setSelectedFilters((prev) => [...prev, newFilter]);
    } else {
      setSelectedFilters((prev) => prev.filter((f) => f.searchFieldId !== id));
    }
  };

  const tabComponents: ITabItem[] = useMemo(
    () => [
      {
        title: 'Current Parameters',
        eventKey: 'defaultParameters',
        content: (
          <div
            id="filterparameterscontainer-defaultparameters-wrapper"
            className="w-full h-full px-3 overflow-y-auto"
          >
            {currentParameters?.map((f) => (
              <LabelRowWithActions
                isVisible={true}
                isChecked={filters?.data.some(
                  (ff) => ff.searchFieldId === f.searchFieldId,
                )}
                onCheckboxChange={onFilterSelectionChange}
                columnId={f.searchFieldId}
                columnTitle={f.columnHeader}
                key={`sf-${f.searchFieldId}`}
              />
            ))}
          </div>
        ),
      },
      {
        title: 'Additional Parameters',
        eventKey: 'additionalParameters',
        content: (
          <div
            id="filterparameterscontainer-additionalparameters-wrapper"
            className="w-full"
          >
            <div
              id="filterparameterscontainer-additionalparameters-info-box"
              className="pt-[4px] pb-[4px] w-[95%]"
            >
              <InfoBox
                title={'When you add additional parameters it will perform search again'}
              />
            </div>
            <div
              id="filterparameterscontainer-additionalparameters-collapsible-panel"
              className="!h-full px-3 pb-3 overflow-y-auto"
            >
              {additionalFilters.map((st: SearchTargetDTO) => {
                const sortedSearchFields = [...st.searchFields].sort((a, b) =>
                  a.displayName.localeCompare(b.displayName),
                );
                const filteredSearchFields = sortedSearchFields.filter(
                  (sf) => !filters.data.some((f) => f.searchFieldId === sf.id),
                );
                return (
                  <CollapsiblePanel title={st.name} key={`st-${st.id}`}>
                    {filteredSearchFields.map((sf: SearchFieldDTO) => {
                      return (
                        <LabelRowWithActions
                          isVisible={true}
                          isChecked={selectedFilters?.some(
                            (ff) => ff.searchFieldId === sf.id,
                          )}
                          onCheckboxChange={onAdditionalFilterSelectionChange}
                          columnId={sf.id}
                          columnTitle={sf.displayName}
                          key={`sf-${sf.id}`}
                        />
                      );
                    })}
                  </CollapsiblePanel>
                );
              })}
            </div>
          </div>
        ),
      },
    ],
    [filters, currentParameters, selectedFilters],
  );

  useEffect(() => {
    setHandleOnTabBackPress(() => () => {
      setContentViewState(FilterViews.SELECTED_PARAMETERS);
    });
  }, [setHandleOnTabBackPress]);

  return (
    <RMTabs
      tabItems={tabComponents}
      defaultActiveKey={'defaultParameters'}
      id={'parameter-selection-container'}
    />
  );
};

export default withTabBackButton(FilterParametersContainer);
